import { useEffect, useState } from 'react';
import { Document, documentsAPI } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, Upload } from 'lucide-react';
import FileDropZone from '../../components/common/FileDropZone';
import { toast } from 'sonner';

interface UploadVersionDialogProps {
  open: boolean;
  document: Document;
  onClose: () => void;
  onSuccess: () => void;
}

// CR-5328: New-version upload sub-dialog, opened from the Versions tab of
// DocumentDetailModal.
export default function UploadVersionDialog({ open, document, onClose, onSuccess }: UploadVersionDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setVersion('');
      setNotes('');
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please choose a file');
      return;
    }
    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('version', version);
      formData.append('notes', notes);
      await documentsAPI.uploadNewVersion(document.id, formData);
      onSuccess();
    } catch (err) {
      console.error('Error uploading new version:', err);
      toast.error(getApiErrorMessage(err, 'Failed to upload new version'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Upload new version</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            The current file will be saved as a snapshot in the version history. Replacing it now: <strong>{document.title}</strong>.
          </p>
          <div className="space-y-2">
            <Label htmlFor="ver-file">
              File <span className="text-red-500">*</span>
            </Label>
            {/* Customer review 11 Sep (drag and drop everywhere). */}
            <FileDropZone size="compact" inputId="ver-file" disabled={saving} onFiles={(files) => setFile(files[0] ?? null)} />
            {file && (
              <p className="text-xs text-muted-foreground">
                {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="ver-label">Version label</Label>
            <Input
              id="ver-label"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="e.g. 2.0, v1.1"
            />
            <p className="text-xs text-muted-foreground">
              Will become the document's current version.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ver-notes">Change notes</Label>
            <Textarea
              id="ver-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Optional — what changed in this version?"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !file}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload version
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
